// src/pages/AppOrdenCobroRemitosTable.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import { useFilters, useSortBy, usePagination } from "react-table";
import moment from "moment";
import Table from "../components/TableContainer";

import ModalVerOrdenCobroRemitos from "../components/ModalVerOrdenCobroRemitos";

import {
  getOrdenesCobroRemitos,    // GET /ordencobroremitos
  anularOrdenCobroRemito,    // POST /ordencobroremitos/:id/anular
} from "../helpers/rutaOrdenCobroRemitos";

import { generarPDFOrdenCobroRemito } from "../helpers/pdfOrdenCobroRemito";

// import logoSanPancracio from "../Public/SanPancracio.png";

const Styles = styled.div`
  .table-container { overflow: auto; height: 600px; }
  .table-container table {
    border: 1px solid black;
    border-spacing: 0;
    font-size: 13px;
    table-layout: fixed;
    width: max-content;
  }
  th, td {
    padding: 0.5rem;
    border-bottom: 1px solid black;
    border-right: 1px solid black;
    white-space: nowrap;
  }
  th { background-color: #778899; position: sticky; top: 0; z-index: 2; text-align: center; font-size: 12px; }
  td { background-color: white; }
  .pagination { padding: 0.5rem; background-color: #778899; font-size: 15px; font-weight: bold; }

  /* Anchos claves (ajustá si querés) */
  .table-container table th:nth-child(4),
  .table-container table td:nth-child(4) { width: 120px; min-width: 120px; } /* N° */
  .table-container table th:nth-child(5),
  .table-container table td:nth-child(5) { width: 100px; min-width: 100px; } /* Fecha */
  .table-container table th:nth-child(6),
  .table-container table td:nth-child(6) { width: 220px; min-width: 220px; } /* Cliente */
  .table-container table th:nth-child(7),
  .table-container table td:nth-child(7) { width: 160px; min-width: 160px; } /* Proveedor (opcional) */
  .table-container table th:nth-child(8),
  .table-container table td:nth-child(8),
  .table-container table th:nth-child(9),
  .table-container table td:nth-child(9),
  .table-container table th:nth-child(10),
  .table-container table td:nth-child(10),
  .table-container table th:nth-child(11),
  .table-container table td:nth-child(11) {
    width: 140px; min-width: 140px; text-align: right;
  }
`;

const DEFAULT_LOGO_URL = "/SanPancracio.png";   // 👈 así, sin /public

const EMPRESA_SP = {
  nombre: "SAN PANCRACIO SRL",
  linea1: "Avda. Peron 1600 - Oficina 29 Piso 1 - Yerba Buena - Tucumán",
  linea2: "Tel: 0381-3008829 • contacto@sanpancracio.com",
};

export default function AppOrdenCobroRemitosTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [desde, setDesde] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [hasta, setHasta] = useState(moment().format("YYYY-MM-DD"));
  const [estado, setEstado] = useState(""); // "", "pendiente", "anulada"

  const [showVer, setShowVer] = useState(false);
  const [ocId, setOcId] = useState(null);

  const money = (n) =>
    Number(n || 0).toLocaleString("es-AR", { style: "currency", currency: "ARS" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    const resp = await getOrdenesCobroRemitos({ desde, hasta });
    if (!resp?.ok) {
      console.error(resp?.msg || resp?.error || "Error al listar OCR");
      setData([]);
    } else {
      let ordenes = resp.ordenes || [];
      if (estado) {
        const expected = estado.toLowerCase();
        ordenes = ordenes.filter(oc => (oc.estado || "").toLowerCase() === expected);
      }
      setData(ordenes);
    }
    setLoading(false);
  }, [desde, hasta, estado]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const onAnular = useCallback(async (id) => {
    if (!window.confirm("¿Anular esta Orden de Cobro de Remitos? Esto revierte sus aplicaciones.")) return;
    const r = await anularOrdenCobroRemito(id);
    if (r?.ok) {
      alert("Orden anulada.");
      fetchData();
    } else {
      alert(r?.msg || r?.error || "No se pudo anular la orden.");
    }
  }, [fetchData]);

  const findByNumeroYAbrir = async (imprimir = false) => {
    const nro = prompt("N° de Orden (ej: OCR-2025-0007):");
    if (!nro) return;
    const r = await getOrdenesCobroRemitos({});
    if (!r?.ok) return alert(r?.msg || "Error buscando órdenes.");
    const found = (r.ordenes || []).find(
      x => (x.numero || "").trim().toUpperCase() === nro.trim().toUpperCase()
    );
    if (!found) return alert("No se encontró la Orden.");

    if (imprimir) {
      generarPDFOrdenCobroRemito(found._id, {
        logoUrl: DEFAULT_LOGO_URL,
        empresa: EMPRESA_SP,
      });
    } else {
      setOcId(found._id);
      setShowVer(true);
    }
  };


  const columns = useMemo(() => [
    {
      id: "ver",
      Header: "Ver",
      accessor: "_id",
      width: 50,
      Cell: ({ row }) => (
        <button
          className="action-btn action-btn--edit"
          title="Ver"
          onClick={() => { setOcId(row.original._id); setShowVer(true); }}
        >
          <i className="fa fa-eye" aria-hidden="true" />
        </button>
      ),
    },
    {
      id: "pdf",
      Header: "PDF",
      accessor: "_id",
      width: 50,
      Cell: ({ row }) => (
        <button
          className="action-btn action-btn--pdf"
          title="Generar PDF"
          onClick={() =>
            generarPDFOrdenCobroRemito(row.original._id, {
              logoUrl: DEFAULT_LOGO_URL,
              empresa: EMPRESA_SP,
            })
          }
        >
          <i className="fa fa-file-pdf-o" aria-hidden="true" />
        </button>
      ),
    },

    {
      id: "anular",
      Header: "Anular",
      accessor: "_id",
      width: 65,
      Cell: ({ row }) => (
        <button
          className="action-btn action-btn--del"
          title="Anular OCR"
          disabled={(row.original.estado || "").toLowerCase() === "anulada"}
          onClick={() => onAnular(row.original._id)}
        >
          <i className="fa fa-ban" aria-hidden="true" />
        </button>
      ),
    },

    { Header: "N° OCR", accessor: "numero", width: 120 },

    {
      Header: "Fecha",
      accessor: (r) => (r.fecha ? new Date(r.fecha) : ""),
      id: "fecha",
      width: 110,
      Cell: ({ value }) =>
        value instanceof Date ? value.toLocaleDateString("es-AR") : "-",
    },

    // Cliente principal de la OCR
    { Header: "Cliente", accessor: "cliente.razonsocial", width: 220 },

    // Proveedor opcional (si filtraste por proveedor al crear OCR)
    {
      Header: "Proveedor",
      id: "proveedor",
      accessor: (r) => r?.proveedor?.razonsocial || "",
      width: 180,
    },

    // Remitos aplicados
    {
      Header: () => <div style={{ textAlign: "center" }}>Remito</div>,
      id: "remitos",
      accessor: (r) =>
        (r.aplicaciones || [])
          .map(a => a?.remito?.codrem)
          .filter(Boolean)
          .map(n => `#${String(n).padStart(6, "0")}`)
          .join(", "),
      width: 160,
      Cell: ({ value }) => (
        <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {value}
        </div>
      ),
    },

    // Totales
    {
      Header: () => <div style={{ textAlign: "center" }}>Σ Aplicado</div>,
      accessor: (r) => r?.totales?.aplicado ?? 0,
      id: "aplicado",
      width: 140,
      Cell: ({ value }) => <div style={{ textAlign: "right" }}>{money(value)}</div>,
      Footer: ({ rows }) => {
        const t = rows.reduce((s, r) => s + (r.values.aplicado || 0), 0);
        return <div style={{ textAlign: "right", fontWeight: "bold", color: "red" }}>{money(t)}</div>;
      },
    },
    {
      Header: () => <div style={{ textAlign: "center" }}>Σ Cobros</div>,
      accessor: (r) => (Array.isArray(r.pagos) ? r.pagos.reduce((a, m) => a + Number(m.importe || 0), 0) : 0),
      id: "cobros",
      width: 140,
      Cell: ({ value }) => <div style={{ textAlign: "right" }}>{money(value)}</div>,
      Footer: ({ rows }) => {
        const t = rows.reduce((s, r) => s + (r.values.cobros || 0), 0);
        return <div style={{ textAlign: "right", fontWeight: "bold", color: "blue" }}>{money(t)}</div>;
      },
    },
    {
      // si manejás “retenciones/descuentos” en OCR, quedan acá;
      // si no, esto va a ser 0 siempre y podés quitar la columna.
      Header: () => <div style={{ textAlign: "center" }}>Σ Deducciones</div>,
      accessor: (r) => (Array.isArray(r.retenciones) ? r.retenciones.reduce((a, m) => a + Number(m.importe || 0), 0) : 0),
      id: "deducciones",
      width: 140,
      Cell: ({ value }) => <div style={{ textAlign: "right" }}>{money(value)}</div>,
      Footer: ({ rows }) => {
        const t = rows.reduce((s, r) => s + (r.values.deducciones || 0), 0);
        return <div style={{ textAlign: "right", fontWeight: "bold", color: "green" }}>{money(t)}</div>;
      },
    },
    {
      Header: () => <div style={{ textAlign: "center" }}>Σ Neto</div>,
      accessor: (r) => (r?.totales?.netoCobrado ?? ( // fallback por si tu back no lo calcula
        (Array.isArray(r.pagos) ? r.pagos.reduce((a, m) => a + Number(m.importe || 0), 0) : 0) -
        (Array.isArray(r.retenciones) ? r.retenciones.reduce((a, m) => a + Number(m.importe || 0), 0) : 0)
      )),
      id: "neto",
      width: 140,
      Cell: ({ value }) => <div style={{ textAlign: "right" }}>{money(value)}</div>,
      Footer: ({ rows }) => {
        const t = rows.reduce((s, r) => s + (r.values.neto || 0), 0);
        return <div style={{ textAlign: "right", fontWeight: "bold", color: "#333" }}>{money(t)}</div>;
      },
    },

    {
      Header: () => <div style={{ textAlign: "center" }}>Estado</div>,
      accessor: "estado",
      width: 120,
      Cell: ({ value }) => (String(value || "").toLowerCase() === "anulada" ? "ANULADA" : "PENDIENTE"),
    },
  ], [onAnular]);

  return (
    <div className="container mt-4">
      <h3 className="text-center mb-4">ÓRDENES DE COBRO (REMITOS)</h3>

      {/* Filtros / acciones */}
      <div className="mb-3" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <div className="input-group input-group-sm" style={{ width: 200 }}>
          <span className="input-group-text" style={{ fontSize: 14, padding: '8px 12px' }}>Desde</span>
          <input type="date" className="form-control" style={{ fontSize: 14, padding: '8px 12px' }}
            value={desde} onChange={e => setDesde(e.target.value)} />
        </div>
        <div className="input-group input-group-sm" style={{ width: 200 }}>
          <span className="input-group-text" style={{ fontSize: 14, padding: '8px 12px' }}>Hasta</span>
          <input type="date" className="form-control" style={{ fontSize: 14, padding: '8px 12px' }}
            value={hasta} onChange={e => setHasta(e.target.value)} />
        </div>

        <select
          className="form-select form-select-sm"
          style={{ fontSize: 14, padding: '8px 12px', width: 170 }}
          value={estado}
          onChange={e => setEstado(e.target.value)}
        >
          <option value="">Todos</option>
          <option value="pendiente">Pendiente</option>
          <option value="anulada">Anulada</option>
        </select>

        <button className="btn btn-primary btn-sm" style={{ fontSize: 14, padding: '8px 12px' }} onClick={fetchData}>
          Buscar
        </button>

        {/* utilidades por N° */}
        <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: 14, padding: '8px 12px' }}
          onClick={() => findByNumeroYAbrir(false)}>
          Ver por N°
        </button>
        <button className="btn btn-outline-success btn-sm" style={{ fontSize: 14, padding: '8px 12px' }}
          onClick={() => findByNumeroYAbrir(true)}>
          Imprimir por N°
        </button>
      </div>

      <Styles>
        <div className="table-container">
          <Table
            columns={columns}
            data={data}
            loading={loading}
            plugins={[useFilters, useSortBy, usePagination]}
          />
        </div>
      </Styles>

      {showVer && ocId && (
        <ModalVerOrdenCobroRemitos
          show={showVer}
          ocId={ocId}
          onHide={(refrescar) => {
            setShowVer(false);
            setOcId(null);
            if (refrescar) fetchData();
          }}
        />
      )}
    </div>
  );
}
